import { visuallyHidden } from "@mui/utils";
import { FAQS_PATH } from "../../routes";

export const mobilePaymentsFaqsLinkClassName = "mobile-payments-faqs-link";

const renderMobilePaymentsFaqsLink = (label, srText) => (
  <li>
    <a
      href={FAQS_PATH}
      target="_blank"
      rel="noopener noreferrer"
      className={mobilePaymentsFaqsLinkClassName}
    >
      <b>{label}</b>
      <span style={visuallyHidden}> {srText}</span>
    </a>
  </li>
);

const countryDrawer = {
  en: {
    priorityCountries: {
      USA: "Discover® is accepted at 99% of places that take credit cards nationwide.*",
      JPN: "You can use your card at many locations here, including many top merchants in Tokyo, Osaka, and Kyoto.",
      PER: "You can use your card at many locations here, including many top merchants in Lima.",
      ESP: "You can use your card at many locations here, including many top merchants in Barcelona, Madrid, and Seville.",
      URY: "You can use your card at a growing number of locations here, including many top merchants in Montevideo, Punta del Este, and Colonia del Sacramento.",
      ARG: "You can use your card at many locations here, including many top merchants in Buenos Aires and Mendoza.",
      CHL: "You can use your card at many locations here, including many top merchants in Santiago.",
      DEU: "You can use your card at many locations here, including many top merchants in Berlin.",
      MEX: "You can use your card at many locations here, including many top merchants in Cancun.",
      GBR: "You can use your card at many locations here, including many top merchants in London.",
      CAN: "You can use your card at a growing number of locations here, including many top merchants in Vancouver and Toronto.",
      ITA: "You can use your card at a growing number of locations here, including many top merchants in Rome.",
      PRT: "You can use your card at a growing number of locations here, including many top merchants in Lisbon.",
      AUS: "You can use your card at a limited number of locations here, including many top merchants in Sydney, Melbourne, and Brisbane.",
      FRA: "You can use your card at a growing number of locations here, including many top merchants in Paris.",
    },
    /** USA acceptance copy with footnote: visible in modal + sr-only inline. Use priorityCountries.USA elsewhere. */
    usaAcceptanceWithFootnote: (
      <>
        Discover® is accepted at 99% of places that take credit cards
        nationwide.*
        <span style={visuallyHidden}>
          {" "}
          According to the Feb 2026 issue of the Nilson Report.
        </span>
      </>
    ),
    acceptanceMarkHeadline: {
      coverage: "Take your card wherever you go",
      noCoverage: "Traveling in the region? Take your card",
    },
    aboveMarksCopy: {
      coverage: "What to look for when you pay in ",
      noCoverage: {
        Europe: "What to look for when you pay in Europe",
        Africa: "In-network Payments in the Middle East & Africa",
        Asia: "In-network Payments in Asia Pacific",
        America: "What to look for when you pay in the Americas",
        Antarctica: "What to look for when you pay in the Americas",
      },
    },
    acceptanceATMcoverage:
      "Your card is part of a global network, so you can use it anywhere you see these logos. Don't see any? Just ask.",
    noAcceptanceATMcoverage: {
      Europe:
        "Your card is part of a growing global network. You can use your card across some of Europe's most popular destinations, including Spain, Germany, the UK, Italy, Portugal and France.",
      Africa:
        "Your card is part of a growing global network. Use your card in the UAE, Egypt, Morocco and South Africa. With destinations in the Middle East & Africa expanding rapidly, you can use your card in more and more places here.",
      Asia: "Your card is part of a growing global network. Use your card almost everywhere in Japan as well as top travel destinations across Asia and Pacific Islands, including India, China, Singapore, Australia and more.",
      America:
        "Your card is part of a growing global network. You can use your card across Canada, Mexico and the U.S., as well as in popular South American travel spots like Peru, Uruguay, Argentina, Chile and more.",
      Antarctica:
        "Your card is part of a growing global network. You can use your card across Canada, Mexico and the U.S., as well as in popular South American travel spots like Peru, Uruguay, Argentina, Chile and more.",
    },
    regions: {
      Europe: "Europe",
      Africa: "the Middle East & Africa",
      Asia: "Asia Pacific",
      America: "the Americas",
    },
    atm: {
      coverage: {
        headline: "Need cash while you’re here?",
        subtitle: "Use your card to get local currency at these ATMs:",
        supplmentary:
          "Plus, any ATM displaying the Discover® Network, Diners Club International®, or PULSE® logo.",
        fees: "Fees and limits may apply. PIN may also be required.",
        footnote: "Internal DFS Services LLC Data as of 12/31/2025",
      },
      coverageAlt: {
        headline: "You can use your card at these ATMs",
        subtitle: "Use your card to get local currency at these ATMs*:",
        supplmentary:
          "Plus, any ATM displaying the Discover® Network, Diners Club International®, or PULSE® logo.",
        fees: "Fees and limits may apply. PIN may also be required.",
      },
      noCoverage: {
        headline: "ATM coverage is emerging",
        subtitle: "We are working to establish ATM acceptance in this country.",
      },
    },
    extraChina: {
      headline: "Heading to China soon?",
      copy: (
        <>
          Enjoy cash-free payments with Alipay and Weixin Pay*
          <span style={visuallyHidden}>
            {" "}
            The official name of the mobile payment app is Weixin Pay. WeChat is
            the international version of Weixin for users traveling to China.
            Within China, the app is known as Weixin.
          </span>
          —the region's preferred mobile payment apps. Simply download Alipay
          and WeChat from the app store and add your card to the apps before
          your trip!
        </>
      ),
      faqsLink: renderMobilePaymentsFaqsLink(
        "See Frequently Asked Questions",
        "Opens in a new browser window"
      ),
      disclosure:
        "The official name of the mobile payment app is Weixin Pay. WeChat is the international version of Weixin for users traveling to China. Within China, the app is known as Weixin.",
    },
    /** Headline template for Singapore, Malaysia, Thailand, Philippines. Use {country} placeholder. */
    extraSoutheastAsia: {
      headlineTemplate: "Heading to {country} soon?",
    },
    extraThailand: {
      headline: "Heading to Thailand soon?",
      copy: (
        <>
          Enjoy cash-free payments with GrabPay—the region's preferred mobile
          payment app. Simply download GrabPay from the app store and add your
          card to the apps before your trip!
        </>
      ),
      faqsLink: renderMobilePaymentsFaqsLink(
        "See Frequently Asked Questions",
        "Opens in a new browser window"
      ),
    },
  },
  es: {
    priorityCountries: {
      USA: "Discover® es aceptada en el 99% de los lugares que aceptan tarjetas de crédito en todo el país.*",
      JPN: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en Tokio, Osaka y Kioto. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      PER: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en Lima. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      ESP: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en Barcelona, Madrid y Sevilla. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      URY: "Puedes usar tu tarjeta en un número creciente de lugares aquí, incluyendo muchos de los principales comercios en Montevideo, Punta del Este y Colonia del Sacramento. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      ARG: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en Buenos Aires y Mendoza. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      CHL: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en Santiago. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      DEU: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en Berlin. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      MEX: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en Cancún. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      GBR: "Puedes usar tu tarjeta en muchos lugares aquí, incluyendo muchos de los principales comercios en London. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      CAN: "Puedes usar tu tarjeta en un número creciente de lugares aquí, incluyendo muchos de los principales comercios en Vancouver y Toronto. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      ITA: "Puedes usar tu tarjeta en un número creciente de lugares aquí, incluyendo muchos de los principales comercios en Roma. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      PRT: "Puedes usar tu tarjeta en un número creciente de lugares aquí, incluyendo muchos de los principales comercios en Lisboa. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      AUS: "Puedes usar tu tarjeta en un número limitado de lugares aquí, incluyendo muchos de los principales comercios en Sídney, Melbourne y Brisbane. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
      FRA: "Puedes usar tu tarjeta en un número creciente de lugares aquí, incluyendo muchos de los principales comercios en París. Busca estos logotipos en el momento de pagar para saber si tu tarjeta es aceptada:",
    },
    /** USA acceptance copy with footnote: visible in modal + sr-only inline. Use priorityCountries.USA elsewhere. */
    usaAcceptanceWithFootnote: (
      <>
        Discover® es aceptada en el 99% de los lugares que aceptan tarjetas de
        crédito en todo el país.*
        <span style={visuallyHidden}>
          {" "}
          Según la edición de febrero de 2026 del Informe Nilson.
        </span>
      </>
    ),
    acceptanceMarkHeadline: {
      coverage: "¿Tienes una de estas tarjetas? Puedes usarla en",
      noCoverage:
        "¿Tienes una de estas tarjetas? Busca aceptación en esta región",
    },
    aboveMarksCopy: {
      coverage: "¿Qué buscar cuando paga en ",
      noCoverage: {
        Europe: "¿Qué buscar cuando paga en Europa?",
        Africa: "Pagos dentro de la red en Medio Oriente y África",
        Asia: "Pagos dentro de la red en Asia Pacífico",
        America: "¿Qué buscar cuando paga en América?",
        Antarctica: "¿Qué buscar cuando paga en América?",
      },
    },
    acceptanceATMcoverage:
      "Como eres parte de la Red Global Discover, puedes viajar con confianza.",
    noAcceptanceATMcoverage: {
      Europe:
        "Como eres parte de la Red Global Discover, puedes usar tu tarjeta en algunos de los destinos más populares de Europa, incluyendo España, Alemania, el Reino Unido, Italia, Portugal y Francia.",
      Africa:
        "Como eres parte de la Red Global Discover, puedes usar tu tarjeta en los Emiratos Árabes Unidos, Egipto, Marruecos y Sudáfrica. Con los destinos en Oriente Medio y África expandiéndose rápidamente, puedes usar tu tarjeta en cada vez más lugares aquí.",
      Asia: "Como eres parte de la Red Global Discover, puedes usar tu tarjeta casi en cualquier lugar de Japón, así como en los principales destinos de viaje en Asia y las Islas del Pacífico, incluyendo India, China, Singapur, Australia y más.",
      America:
        "Como eres parte de la Red Global Discover, puedes usar tu tarjeta en Canadá, México y los EE. UU., así como en destinos populares de América del Sur como Perú, Uruguay, Argentina, Chile y más.",
      Antarctica:
        "Como eres parte de la Red Global Discover, puedes usar tu tarjeta en Canadá, México y Estados Unidos, así como en destinos populares de América del Sur como Perú, Uruguay, Argentina, Chile y más.",
    },
    regions: {
      Europe: "Europa",
      Africa: "Medio Oriente y África",
      Asia: "Asia Pacífico",
      America: "América",
    },
    atm: {
      coverage: {
        headline: "¿Necesita dinero en efectivo mientras está aquí?",
        subtitle:
          "Use su tarjeta para obtener moneda local en estos cajeros automáticos:",
        supplmentary:
          "Además, cualquier cajero automático que muestre el logo de Discover® Network, Diners Club International® o PULSE®.",
        fees: "Se pueden aplicar tarifas y límites. También puede ser necesario un PIN.",
        footnote: "Datos internos de DFS Services LLC como de 31/12/2025",
      },
      coverageAlt: {
        headline: "Puede usar su tarjeta en estos cajeros automáticos",
        subtitle:
          "Use su tarjeta para obtener moneda local en estos cajeros automáticos:",
        supplmentary:
          "Además, cualquier cajero automático que muestre el logo de Discover® Network, Diners Club International® o PULSE®.",
        fees: "Se pueden aplicar tarifas y límites. También puede ser necesario un PIN.",
      },
      noCoverage: {
        headline: "La cobertura de cajeros automáticos está creciendo",
        subtitle:
          "Estamos trabajando para establecer la aceptación en cajeros automáticos en este país.",
      },
    },
    extraChina: {
      headline: "¿A punto de ir a China?",
      copy: (
        <>
          Disfruta de pagos sin efectivo con Alipay y Weixin Pay*
          <span style={visuallyHidden}>
            {" "}
            El nombre oficial de la aplicación de pago móvil es Weixin Pay.
            WeChat es la versión internacional de Weixin para usuarios que
            viajan a China. Dentro de China, la aplicación se conoce como
            Weixin.
          </span>
          —las aplicaciones de pago móvil preferidas en la región. Simplemente
          descarga Alipay y WeChat desde la tienda de aplicaciones y añade tu
          tarjeta a las aplicaciones antes de tu viaje!
        </>
      ),
      faqsLink: renderMobilePaymentsFaqsLink(
        "Ver preguntas frecuentes",
        "Se abre en una nueva ventana del navegador"
      ),
      disclosure:
        "El nombre oficial de la aplicación de pago móvil es Weixin Pay. WeChat es la versión internacional de Weixin para usuarios que viajan a China. Dentro de China, la aplicación se conoce como Weixin.",
    },
    /** Headline template for Singapore, Malaysia, Thailand, Philippines. Use {country} placeholder. */
    extraSoutheastAsia: {
      headlineTemplate: "¿A punto de ir a {country}?",
    },
    extraThailand: {
      headline: "¿A punto de ir a Tailandia?",
      copy: (
        <>
          Disfruta de pagos sin efectivo con GrabPay * —la aplicación de pago
          móvil preferida en la región. Simplemente descarga GrabPay desde la
          tienda de aplicaciones y añade tu tarjeta a la aplicación antes de tu
          viaje!*
        </>
      ),
      faqsLink: renderMobilePaymentsFaqsLink(
        "Ver preguntas frecuentes",
        "Se abre en una nueva ventana del navegador"
      ),
    },
  },
};

export default countryDrawer;
